import axios from 'axios';
import { getBearerToken } from './helpers';

let configLoaded = false;

window.$wx = window.$wx || {};

async function fetchConfig(url) {
    try {
        const response = await axios.get(url, {
            headers: {
                'Cache-Control': 'no-cache, max-age=0, must-revalidate',
            },
        });
        if (typeof response.data === 'string' || response.data instanceof String) {
            return JSON.parse(response.data);
        }
        if (!response.data) {
            return undefined;
        }
        return response.data;
    } catch (error) {
        return undefined;
    }
}

export const getConfig = async () => {
    if (configLoaded) {
        return window.$wx.config;
    } else {
        window.$wx.config = window.$wx.config || {};
        window.$wx.config.data = await fetchConfig('/config/env_vars.dev.json');

        if (!window.$wx.config.data) {
            window.$wx.config.data = await fetchConfig('/config/env_vars.json');
        }

        configLoaded = true;
        return window.$wx.config;
    }
};

/**
 * Fetches the DSM structure of the current application.
 *
 * @param {string} appSlug      - The app slug.
 * @returns {Promise<any>}      - The DSM structure.
 */
export const getAppDsmStructure = async (appSlug) => {
    const orchestratorConfig = await getConfig();
    const url = `${orchestratorConfig.data.API_ROUTER_URL}/v1/dsm/v1/${appSlug}`;
    const result = await axios.get(url);

    return result?.data?.data || {};
};

/**
 * Fetches the active feature flags for the app
 *
 * @param {string} appSlug The client's app slug.
 * @returns {Promise<string[]>} True if enabled, false otherwise.
 */
export const getFeatureFlags = async (appSlug) => {
    const orchestratorConfig = await getConfig();
    const url = `${orchestratorConfig.data.API_ROUTER_URL}/v2/forms/features/${appSlug}`;

    const result = await axios.get(url);
    return result?.data || [];
};

/**
 * Checks if the given feature flag is enabled for the given app slug.
 *
 * @param {string} featureFlag The feature flag to check for.
 * @param {string} appSlug The client's app slug.
 * @returns {Promise<boolean>} True if enabled, false otherwise.
 */
export const isFeatureFlagEnabled = async (featureFlag, appSlug) => {
    const orchestratorConfig = await getConfig();
    const url = `${orchestratorConfig.data.API_ROUTER_URL}/v1/platform/applications/${appSlug}/features`;
    const params = { featureFlag };

    const result = await axios.get(url, { params });
    return result?.data?.isEnabled;
};

/**
 * Fetches the menu items for the given app slug.
 *
 * @param {string} appSlug The app slug to fetch the menu items for.
 * @returns {Promise<object>} The fetched menu items if successful, otherwise an empty object.
 */
export const fetchMenuItems = async (appSlug) => {
    let result = {};

    const token = getBearerToken();

    if (!token?.token_type || !token?.access_token) {
        return result;
    }

    const orchestratorConfig = await getConfig();
    const url = `${orchestratorConfig.data.API_ROUTER_URL}/v2/forms/menu/${appSlug}`;

    result = await axios.get(url, {
        headers: {
            authorization: `${token.token_type} ${token.access_token}`,
        },
        timeout: 7000,
    });

    if (!(parseInt(result?.data?.menuItems?.length) > 0)) {
        throw new Error(`Got an empty dataset of menu items for ${appSlug}!`);
    }

    return result?.data || {};
};

/**
 * Fetches the configuration details of the current application.
 *
 * @param {string} appRootUrl   - The URL of the application root.
 * @param {string} appSlug      - The app slug.
 * @returns {Promise<any>}      - The current application's configuration object.
 */
export const getAppConfig = async (appRootUrl, appSlug) => {
    const orchestratorConfig = await getConfig();
    const url = `${orchestratorConfig.data.API_ROUTER_URL}/v2/forms/app-config/${appSlug}`;
    try {
      const result = await axios.get(url);
      return Object.keys(result.data || {}).length > 0
        ? result.data
        : await getAppConfigFile(appRootUrl, appSlug)
    } catch (err) {
        console.error(`Error fetching app config from database in orchestrator: ${err}`);
        return await getAppConfigFile(appRootUrl, appSlug);
    }
};

/**
* Gets the app config from the build directory of wxPortal
*/
const getAppConfigFile = async (appRootUrl, appSlug) => {
  const module = await System.import(
      `${appRootUrl}/assets/config/${appSlug}.json?t=1698414121`
  );
  return module.default;
}


export const getLocales = async (appSlug) => {
    const orchestratorConfig = await getConfig();
    const url = `${orchestratorConfig.data.API_ROUTER_URL}/v2/forms/site/${appSlug}/locales`;
    const result = await axios.get(url);
    if (result.status !== 200) {
      console.error(`Unable to fetch site locales: ${result.status}`, result.status);
    }
    return await result.data || [];
};